import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Mail, Lock, User, Phone, Loader2, Sparkles, AlertCircle, CheckCircle, ArrowLeft, Check } from 'lucide-react';
import { API_URL } from '../../config';
import EmailVerifyForm from './EmailVerifyForm';
import CountrySelect from './CountrySelect';
import { COUNTRIES, DEFAULT_COUNTRY_ISO2 } from '../../data/countries';

// Deux methodes d'inscription au choix (demande utilisateur 2026-08-16) : Email (avec code
// de verification a 6 chiffres) ou Telephone malgache (format-valide uniquement, activation
// immediate - pas de service SMS paye). Voir register_route.py pour la logique serveur.
const SIGNUP_METHODS = [
  {
    id: 'email',
    Icon: Mail,
    title: 'Email',
    subtitle: 'Un code de vérification vous sera envoyé par email',
    gradient: 'from-blue-500 via-cyan-400 to-blue-500',
    ring: 'ring-blue-400/60',
    glow: 'shadow-[0_0_45px_-8px_rgba(56,189,248,0.65)]',
  },
  {
    id: 'phone',
    Icon: Phone,
    title: 'Téléphone',
    subtitle: 'Inscription rapide avec votre numéro',
    gradient: 'from-purple-500 via-fuchsia-400 to-purple-500',
    ring: 'ring-purple-400/60',
    glow: 'shadow-[0_0_45px_-8px_rgba(217,70,239,0.65)]',
  },
];

export default function Signup({ onLogin }) {
  const navigate = useNavigate();
  const [method, setMethod] = useState(null); // null | 'email' | 'phone'
  const [selecting, setSelecting] = useState(null); // id en cours de selection (effet transitoire)
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [countryIso2, setCountryIso2] = useState(DEFAULT_COUNTRY_ISO2);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingUserId, setPendingUserId] = useState(null); // non-null => étape vérification email

  const chooseMethod = (id) => {
    if (selecting) return;
    setError('');
    setSelecting(id);
    setTimeout(() => {
      setMethod(id);
      setSelecting(null);
    }, 420);
  };

  const changeMethod = () => {
    setMethod(null);
    setError('');
  };

  const performLogin = async (identifier) => {
    try {
      const loginRes = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: identifier, password })
      });
      const loginData = await loginRes.json();

      if (loginData.success) {
        localStorage.setItem('auth_token', loginData.token);
        localStorage.setItem('user_type', loginData.user_type);
        localStorage.setItem('user_id', loginData.user_id);
        localStorage.setItem('user_name', loginData.name);
        onLogin(loginData);
      } else {
        navigate('/login');
      }
    } catch (err) {
      navigate('/login');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (method === 'email' && !email.trim()) {
      setError('Adresse email requise.');
      return;
    }
    if (method === 'phone' && !phone.trim()) {
      setError('Numéro de téléphone requis.');
      return;
    }
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (!acceptTerms) {
      setError('Merci d\'accepter les Conditions Générales et la Politique de Confidentialité.');
      return;
    }

    const dialCode = COUNTRIES.find((c) => c.iso2 === countryIso2)?.dialCode || '';
    const fullPhone = phone.trim() ? `${dialCode}${phone.trim()}` : '';

    setLoading(true);
    try {
      const registerRes = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, phone: fullPhone, signup_method: method })
      });
      const registerData = await registerRes.json();

      if (!registerData.success) {
        setError(registerData.message || 'Erreur lors de la création du compte');
        setLoading(false);
        return;
      }

      if (registerData.email_verified) {
        // Inscription par téléphone : rien à vérifier (pas de service SMS), le compte
        // est actif immédiatement — on connecte directement l'utilisateur.
        await performLogin(method === 'phone' ? fullPhone : email);
      } else {
        // Inscription par email : compte créé mais pas encore activé, étape de
        // vérification du code à 6 chiffres avant de pouvoir se connecter.
        setPendingUserId(registerData.user_id);
      }
    } catch (err) {
      setError('Impossible de contacter le serveur. Réessayez.');
    }
    setLoading(false);
  };

  const handleVerified = () => performLogin(email);

  const RobotVideo = () => (
    <div className="relative w-40 h-40 mx-auto">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="relative z-10 w-40 h-40 rounded-full overflow-hidden border-4 border-gradient-to-br from-blue-500 via-purple-500 to-cyan-500 shadow-2xl">
        <video autoPlay loop muted playsInline className="w-full h-full object-cover">
          <source src="/Teckia-face.mp4" type="video/mp4" />
        </video>
      </div>
      <div className="absolute inset-0 border-4 border-blue-500/30 rounded-full animate-ping" style={{ animationDuration: '2s' }}></div>
      <div className="absolute inset-0 border-4 border-purple-500/30 rounded-full animate-ping" style={{ animationDuration: '3s', animationDelay: '1s' }}></div>
      {[...Array(8)].map((_, i) => (
        <div key={i} className="absolute w-2 h-2 bg-blue-400/50 rounded-full animate-float"
          style={{ top: `${Math.random() * 150}%`, left: `${Math.random() * 150}%`, animationDelay: `${i * 0.3}s`, animationDuration: `${3 + Math.random() * 2}s` }} />
      ))}
    </div>
  );

  const showChooser = !pendingUserId && !method;
  const activeMethod = SIGNUP_METHODS.find((m) => m.id === method);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 animate-float">
          <Brain className="h-16 w-16 text-blue-400/20" />
        </div>
        <div className="absolute bottom-20 right-20 animate-float-delayed">
          <Sparkles className="h-12 w-12 text-purple-400/20" />
        </div>
      </div>

      <div className={`relative z-10 w-full transition-all duration-500 ease-out ${showChooser ? 'max-w-2xl' : 'max-w-md'}`}>
        <div className="text-center mb-8">
          <RobotVideo />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent mb-2">
            Rejoindre TeckiA
          </h1>
          <p className="text-blue-200">
            {showChooser ? 'Choisissez comment créer votre compte' : 'Créez votre compte gratuitement'}
          </p>
        </div>

        <div className="bg-black/40 backdrop-blur-2xl rounded-3xl p-8 border border-white/20 shadow-2xl">
          {pendingUserId ? (
            <EmailVerifyForm
              userId={pendingUserId}
              email={email}
              onVerified={handleVerified}
              onCancel={() => setPendingUserId(null)}
            />
          ) : !method ? (
            <div className="grid sm:grid-cols-2 gap-5">
              {SIGNUP_METHODS.map(({ id, Icon, title, subtitle, gradient, ring, glow }, idx) => {
                const isSelecting = selecting === id;
                const isDimmed = !!selecting && !isSelecting;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => chooseMethod(id)}
                    disabled={!!selecting}
                    style={{ animationDelay: `${idx * 100}ms` }}
                    className={`group relative flex flex-col items-center text-center gap-4 rounded-2xl border p-6 transition-all duration-300 animate-scale-in
                      ${isSelecting ? `scale-105 ring-2 ${ring} ${glow} border-white/40 bg-white/10` : 'border-white/15 bg-white/5 hover:bg-white/10 hover:border-white/30 hover:scale-[1.03]'}
                      ${isDimmed ? 'opacity-40 scale-95' : ''}`}
                  >
                    <div className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110 ${isSelecting ? 'scale-110' : ''}`}>
                      <Icon className="h-8 w-8 text-white" />
                      {isSelecting && (
                        <span className="absolute inset-0 rounded-2xl bg-white/30 animate-ping" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg flex items-center justify-center gap-2">
                        S'inscrire avec {title}
                        {isSelecting && <Check className="h-4 w-4 text-emerald-400" />}
                      </h3>
                      <p className="text-blue-200/60 text-xs mt-1.5 leading-relaxed">{subtitle}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5 animate-scale-in">
            <button
              type="button"
              onClick={changeMethod}
              className="inline-flex items-center gap-1.5 text-blue-300/70 hover:text-blue-200 text-xs font-medium transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Changer de méthode ({activeMethod?.title})
            </button>

            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2 flex items-center gap-2">
                <User className="h-4 w-4" /> Nom complet
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                placeholder="Rakoto Jean"
                required
              />
            </div>

            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2 flex items-center gap-2">
                <Mail className="h-4 w-4" /> Email
                {method === 'phone' && <span className="text-blue-300/40 font-normal">(optionnel)</span>}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                placeholder="votre@email.com"
                required={method === 'email'}
              />
            </div>

            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2 flex items-center gap-2">
                <Phone className="h-4 w-4" /> Téléphone
                {method === 'email' && <span className="text-blue-300/40 font-normal">(optionnel)</span>}
              </label>
              <div className="flex items-stretch bg-white/10 border border-white/20 rounded-xl focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                <CountrySelect value={countryIso2} onChange={setCountryIso2} />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-transparent px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none"
                  placeholder="034 12 345 67"
                  required={method === 'phone'}
                />
              </div>
            </div>

            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2 flex items-center gap-2">
                <Lock className="h-4 w-4" /> Mot de passe
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                placeholder="8 caractères minimum"
                required
              />
            </div>

            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2 flex items-center gap-2">
                <Lock className="h-4 w-4" /> Confirmer le mot de passe
              </label>
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                placeholder="••••••••"
                required
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-1 w-4 h-4 rounded accent-blue-500 flex-shrink-0"
              />
              <span className="text-blue-200/80 text-xs leading-relaxed">
                J'accepte les <a href="/terms" target="_blank" rel="noreferrer" className="text-blue-300 hover:underline">Conditions Générales</a>
                {' '}et la <a href="/privacy" target="_blank" rel="noreferrer" className="text-blue-300 hover:underline">Politique de Confidentialité</a>
              </span>
            </label>

            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                <span className="text-red-300 text-sm">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-4 rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-3 shadow-xl"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Création du compte...
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5" />
                  Créer mon compte gratuitement
                </>
              )}
            </button>
          </form>
          )}

          {!pendingUserId && (
          <div className="text-center mt-6">
            <span className="text-blue-200/60 text-sm">Vous avez déjà un compte ? </span>
            <button
              onClick={() => navigate('/login')}
              className="text-blue-300 hover:text-blue-200 text-sm font-semibold transition-colors"
            >
              Se connecter
            </button>
          </div>
          )}
        </div>

        <div className="text-center mt-6 flex items-center justify-center gap-6">
          <a href="/privacy" className="text-blue-300/50 hover:text-blue-300 text-xs transition-colors">
            Politique de Confidentialité
          </a>
          <span className="text-white/20 text-xs">·</span>
          <a href="/terms" className="text-blue-300/50 hover:text-blue-300 text-xs transition-colors">
            CGU
          </a>
        </div>
      </div>
    </div>
  );
}
